import { type AnyAction } from "~/app/store/types";
import { type ThunkAction, type ThunkDispatch } from "redux-thunk";
import { type RootState } from "~/app/store";
import { type CsticUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import { type LinearConfiguration, type SubstepTile } from "~/features/configurator/Linear/models";
import { type SavedConfigurationDTO, SubstepType } from "~/features/configurator/Linear/dtos";
import { type LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import { constructCsticToKmatTypeMap, csticUpdatesFromDict } from "~/features/configurator/Linear/utils";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_AMOUNT_NAME,
  CSTIC_PART_SIZING_TYPE_NAME,
  CSTIC_PART_SIZING_TYPE_NONE,
  CSTIC_VSKITS_NAME,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  GUIDEWAY_PIECE_PATTERN,
  INPUT_VALIDATION_DELAY,
} from "~/features/configurator/Linear/constants";
import {
  getConfigId,
  getCurrentStep,
  getProductCode,
  isCurrentStepValid,
} from "~/features/configurator/Linear/redux/selectors";
import { sendLinearSaveConfig } from "~/features/configurator/Linear/api/sendLinearSaveConfig";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import {
  trackLCPreviousClick,
  trackLCStepperClick,
  trackLCStepStarted,
} from "~/features/configurator/common/configuratorGA";
import { getExtraValidationParamName } from "~/features/configurator/Linear/namingUtils";
import { LcSummaryTab } from "~/features/configurator/common/LcSummaryTab";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import { getNextStep, getPreviousStep } from "~/features/configurator/linearStepHelper";
import { selectCsticExpandedFromNested } from "~/features/configurator/Linear/redux/selectCstic";
import { RAIL_CONFIGURATOR_PRODUCT_CODE, UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import type { ConfiguratorCsticTarget } from "~/features/configurator/common/api/configuratorCommands";
import {
  prepareClassicRailStepResetTargets,
  prepareGuidewaySetResetTargets,
  prepareUnitCarriageResetTargets,
  prepareUnitCarriageStepResetTargets,
  prepareUnitGuidewayStepResetTargets,
} from "~/features/configurator/Linear/redux/configuratorResetTargets";
import {
  configuratorMutationCoordinator,
  type ConfiguratorMutationPolicy,
} from "~/features/configurator/Linear/redux/configuratorMutationCoordinator";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const LOAD_DATA = "Linear: Load";
export const REPLACE_CONFIGURATION_AFTER_RESET = "Linear: Replace configuration after reset";
export const UPDATE_CSTICS = "Linear: Update characteristic values";
export const UPDATE_ADDITIONAL_DATA = "Linear: Update substep additional data";
export const UPDATE_CSTICS_FROM_TILE = "Linear: Update characteristic values from a clickable tile";
export const RESET_CURRENT_STEP = "Linear: Reset current step";
export const VALIDATE_CURRENT_STEP = "Linear: Validate current step";
export const SET_STEP = "Linear: Set step";
export const SET_TAB = "Linear: Set active tab";
export const CUSTOM_SEGMENT_LENGTH_SELECTED = "Linear: custom segment length selected";
export const CUSTOM_SEGMENT_LENGTH_SELECTED_RESET = "Linear: reset custom segment length selected";
export const UPDATE_SMDS_FLIP = "Linear: update SMDS flip";
export const POST_HYBRIS_UPDATE = "Linear: Synchronize with hybris post update";

export const RESTORE_SNAPSHOT = "Linear: Restore previous valid snapshot";

export const TOGGLE_LINEAR_CARRIAGE_EDIT = "Linear: Toggle Carriage edit";

export const SHOW_LOADER = "Linear: Show loader";
export const HIDE_LOADER = "Linear: Hide loader";
export const UPDATE_QUANTITY = "Linear: Update quantity";
export const UPDATE_PRICE_REFERENCE = "Linear: Price Reference";

export const MARK_ALREADY_ADDED_TO_CART = "Linear: Mark added to cart";

export function showLoader() {
  return {
    type: SHOW_LOADER,
  };
}

export function hideLoader() {
  return {
    type: HIDE_LOADER,
  };
}

export function loadData(data: LinearConfiguration, helpUrl: string, type: ConfiguratorType) {
  configuratorMutationCoordinator.beginConfigurationScope(data.configId);
  return {
    type: LOAD_DATA,
    payload: {
      data,
      helpUrl,
      type,
    },
  };
}

export function setAlreadyAddedToCart(newConfigId: string | null | undefined) {
  if (newConfigId) {
    configuratorMutationCoordinator.beginConfigurationScope(newConfigId);
  }
  return {
    type: MARK_ALREADY_ADDED_TO_CART,
    payload: newConfigId,
  };
}

export function restorePreviousSnapshot(): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: RESTORE_SNAPSHOT,
    });

    const productCode = getState().linearConfigurator.productCode;
    const resetStepApi = getConfiguratorDefinition(productCode).apiCalls.stepReset;

    dispatch(sendUpdateToHybris(resetStepApi, undefined, "supersede"));
  };
}

export function updateCsticValuesFromTile(
  elementType: SubstepType,
  tileIndex: number | null,
  tile: SubstepTile,
): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_CSTICS_FROM_TILE,
      payload: { elementType, tileIndex, tile },
    });

    const state = getState().linearConfigurator;
    const productCode = state.productCode;
    const updateApi = getConfiguratorDefinition(productCode).apiCalls.update;

    const updates = [
      ...csticUpdatesFromDict(tile.cstics, constructCsticToKmatTypeMap(state.kmats)),
      ...csticUpdatesFromDict(tile.csticsToSetOnly ?? {}, constructCsticToKmatTypeMap(state.kmats)),
    ];

    dispatch(sendUpdateToHybris((state) => updateApi(state, updates)));
  };
}

export function updateCsticValuesOld(
  updates: Dict<string | undefined | null>,
  owningSubstep: SubstepType,
): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().linearConfigurator;
    dispatch(updateCsticValues(csticUpdatesFromDict(updates, constructCsticToKmatTypeMap(state.kmats)), owningSubstep));
  };
}

export function updateCsticValues(updates: CsticUpdate[], owningSubstep: SubstepType): ThunkResult<void> {
  return (dispatch, getState) => {
    const filtered = updates.filter((update) => !!update.key);
    if (filtered.length !== updates.length) {
      console.warn("Some updates were filtered out because of missing keys", updates);
    }
    dispatch(updateCsticsAction(filtered, owningSubstep));

    const linearState = getState().linearConfigurator;
    const localOnly = updates.every((update) => {
      const cstic = selectCsticExpandedFromNested({ ...update, index: update.index ?? 0 }, owningSubstep)(linearState);
      return cstic.cstic.fake;
    });
    if (localOnly) {
      return;
    }

    const updateApi = getConfiguratorDefinition(linearState.productCode).apiCalls.update;
    dispatch(sendUpdateToHybris((state) => updateApi(state, filtered)));
  };
}

export function resetCurrentStep(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const currentStep = getCurrentStep(state);

    // SCHAEFF-31278 - Reset in step 0 should do the same as the button on top
    if (currentStep === 0) {
      dispatch(resetConfiguratorAction());
      return;
    }

    const configuratorType = state.linearConfigurator.type!;
    if (
      configuratorType === ConfiguratorType.System &&
      state.linearConfigurator.productCode === UNIT_CONFIGURATOR_PRODUCT_CODE &&
      currentStep === 5
    ) {
      dispatch(resetSegmentLengthSelected());
      dispatch({ type: RESET_CURRENT_STEP });
      dispatch(
        sendUpdateToHybris(
          (linearState) => {
            const resetTargets = prepareUnitGuidewayStepResetTargets(linearState.kmats[KmatType.SubRail]);
            return getConfiguratorDefinition(linearState.productCode).apiCalls.stepReset(linearState, resetTargets);
          },
          undefined,
          "supersede",
        ),
      );
      return;
    }
    if (
      configuratorType === ConfiguratorType.System &&
      state.linearConfigurator.productCode === UNIT_CONFIGURATOR_PRODUCT_CODE &&
      currentStep === 6
    ) {
      dispatch({ type: TOGGLE_LINEAR_CARRIAGE_EDIT, payload: { carriageIndex: undefined, statusEdit: false } });
      dispatch({ type: RESET_CURRENT_STEP });
      dispatch(
        sendUpdateToHybris(
          (linearState) => {
            const resetTargets = prepareUnitCarriageStepResetTargets(
              linearState.kmats[KmatType.SubCarriage],
              linearState.kmats[KmatType.Main],
            );
            return getConfiguratorDefinition(linearState.productCode).apiCalls.stepReset(linearState, resetTargets);
          },
          undefined,
          "supersede",
        ),
      );
      return;
    }
    if (
      configuratorType === ConfiguratorType.Rail &&
      state.linearConfigurator.productCode === RAIL_CONFIGURATOR_PRODUCT_CODE &&
      currentStep === 3
    ) {
      dispatch(resetSegmentLengthSelected());
      dispatch({ type: RESET_CURRENT_STEP });
      dispatch(
        sendUpdateToHybris(
          (linearState) => {
            const resetTargets = prepareClassicRailStepResetTargets(linearState.kmats[KmatType.Main]);
            return getConfiguratorDefinition(linearState.productCode).apiCalls.stepReset(linearState, resetTargets);
          },
          undefined,
          "supersede",
        ),
      );
      return;
    }

    dispatch({
      type: RESET_CURRENT_STEP,
    });

    const resetStepApi = getConfiguratorDefinition(state.linearConfigurator.productCode).apiCalls.stepReset;
    dispatch(sendUpdateToHybris(resetStepApi, undefined, "supersede"));
  };
}

export function sendUpdateToHybris(
  updateFunc: (state: LinearConfiguratorState) => Promise<LinearConfiguration | null>,
  navigationUpdate?: boolean,
  pendingInputSettlement: ConfiguratorMutationPolicy = "require-success",
): ThunkResult<Promise<boolean>> {
  return async (dispatch, getState) => {
    const result = await configuratorMutationCoordinator.run(
      () => performConfiguratorUpdate(dispatch, getState, updateFunc, navigationUpdate),
      pendingInputSettlement,
    );
    if (result.status === "failed") {
      console.error(result.error);
      dispatch(hideLoader());
      return false;
    }
    if (result.status === "blocked") {
      return false;
    }
    return true;
  };
}

export async function performConfiguratorUpdate(
  dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
  getState: () => RootState,
  updateFunc: (state: LinearConfiguratorState) => Promise<LinearConfiguration | null>,
  navigationUpdate?: boolean,
): Promise<void> {
  dispatch(showLoader());
  const requestConfigId = getState().linearConfigurator.configId;
  const updateJson = await updateFunc(getState().linearConfigurator);
  if (getState().linearConfigurator.configId !== requestConfigId) {
    return;
  }
  if (updateJson === null) {
    dispatch(hideLoader());
    return;
  }
  dispatch({
    type: POST_HYBRIS_UPDATE,
    payload: {
      hybrisData: updateJson,
      navigationUpdate,
    },
  });
}

/**
 * Goes to the next step. First validates data in current step
 */
export function nextStep(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().linearConfigurator;
    const nextStep = getNextStep(state.type!, state.currentStep, state.steps, state.nextSteps);
    if (!nextStep) {
      return;
    }
    dispatch(internalGoToStep(state.steps.indexOf(nextStep), "forward"));
  };
}

export function previousStep(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().linearConfigurator;
    const previousStep = getPreviousStep(state.type!, state.currentStep, state.steps, state.previousSteps);
    if (!previousStep) {
      throw new Error("No previous step found");
    }
    dispatch(internalGoToStep(state.steps.indexOf(previousStep), "back"));
  };
}

export function goToStep(index: number): ThunkResult<void> {
  return internalGoToStep(index, "stepper");
}

export function goToCadSummary(): ThunkResult<void> {
  return (dispatch, getState) => {
    const activeTab = getState().linearConfigurator.activeTab;
    const steps = getState().linearConfigurator.steps;
    if (activeTab !== LcSummaryTab.CAD) {
      dispatch(setLCSummaryTabAction(LcSummaryTab.CAD));
    }
    dispatch(internalGoToStep(steps.length - 1, "stepper", false));
  };
}

export function setLCSummaryTabAction(tab: LcSummaryTab) {
  return {
    type: SET_TAB,
    payload: tab,
  } as const;
}

/**
 * Goes back to a previously completed step. No validation done
 * @param index Optional. Index of the step to go to. Otherwise go to the previous one
 */
function internalGoToStep(
  index: number,
  source: "stepper" | "back" | "forward",
  scrollToTop = true,
): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: VALIDATE_CURRENT_STEP,
    });
    const lcState = getState().linearConfigurator;
    const currentStep = lcState.currentStep;

    const forwardStep = currentStep + 1 === index;

    const isStepPossible =
      index >= 0 &&
      index < lcState.steps.length &&
      index !== lcState.currentStep &&
      (lcState.steps[index].active || lcState.currentStep + 1 === index);

    if (!isStepPossible) {
      return;
    }

    const currentHighTide = getState().linearConfigurator.highTideStep;
    const valid = isCurrentStepValid(getState());

    if (!valid) {
      // SCHAEFF-9563 - This should scroll to the first error on the page
      // Giving 16ms to make sure react manages to render out the html first.
      window.setTimeout(() => {
        document.querySelector('[data-message="error"], .Mui-error')?.scrollIntoView({ block: "center" });
      }, 16);
      return;
    }

    void configuratorMutationCoordinator
      .run(async () => {
        dispatch(showLoader());
        dispatch({
          type: SET_STEP,
          payload: index,
        });

        if (scrollToTop) {
          window.scroll(0, 0);
        }

        const updatedState = getState().linearConfigurator;
        const productCode = updatedState.productCode;
        const stepChangeApiCall = getConfiguratorDefinition(productCode).apiCalls.stepChange;
        await performConfiguratorUpdate(
          dispatch,
          getState,
          stepChangeApiCall,
          !forwardStep || currentHighTide === updatedState.highTideStep,
        );
        return productCode;
      })
      .then(async (result): Promise<void> => {
        if (result.status === "failed") {
          console.error(result.error);
          dispatch(hideLoader());
          return;
        }
        if (result.status === "blocked") {
          return;
        }

        const productCode = result.value;
        if (source === "stepper" || source === "back") {
          if (source === "stepper") {
            trackLCStepperClick(productCode, currentStep + 1, index + 1);
          } else if (source === "back") {
            trackLCPreviousClick(productCode, currentStep + 1, index + 1);
          }
        }

        if (index > currentHighTide) {
          // We have finished a step - notify Google Analytics
          for (let i = currentHighTide; i < index; i++) {
            await dispatch(getConfiguratorDefinition(productCode).stepHardcodedData[i].trackCompletedStepAction);
          }
        }
        if (forwardStep && lcState.highTideStep === lcState.currentStep) {
          // index from model is 1 based, while in state it's 0 based
          trackLCStepStarted(lcState.productCode, lcState.currentStep + 1);
        }
      })
      .catch((error) => {
        console.error(error);
        dispatch(hideLoader());
      });
  };
}

// Because there's no better name...
// Need to check these in Unit Configurator because sending out of bounds values causes endless loop.
// It's not caused in Rail configurator though.
// Probably needs the jar update from https://me.sap.com/case/002075129400007206802025/overview
const CSTICS_NOT_TO_SEND_IF_OUT_OF_BOUNDS = [
  (name: string) => name === CSTIC_AL_NAME,
  (name: string) => name === CSTIC_AR_NAME,
  (name: string) => GUIDEWAY_PIECE_PATTERN.test(name),
];

export function flushPendingInputChanges() {
  window.setTimeout(() => void flushAndSettlePendingConfiguratorUpdate(), 200);
}

export function flushAndSettlePendingConfiguratorUpdate(): Promise<boolean> {
  return configuratorMutationCoordinator.flushAndSettle();
}

/*
 * Used so we don't spam the KB with every character inputted.
 * The coordinator owns both the debounce generation and the single in-flight request.
 */
const debouncedSendUpdate = (
  substep: SubstepType,
  dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
  getState: () => RootState,
  updates?: CsticUpdate[],
) => {
  configuratorMutationCoordinator.scheduleDebounced(async () => {
    if (!updates) {
      return;
    }

    const state = getState().linearConfigurator;
    let valid = true;
    if (state.productCode === UNIT_CONFIGURATOR_PRODUCT_CODE) {
      const sensitiveUpdates: CsticUpdate[] = [];
      updates.forEach((update) =>
        CSTICS_NOT_TO_SEND_IF_OUT_OF_BOUNDS.forEach((fun) => {
          if (fun(update.key)) {
            sensitiveUpdates.push(update);
          }
        }),
      );
      sensitiveUpdates.forEach((update) => {
        const extraParamName = getExtraValidationParamName(update);
        valid = valid && state.substeps[substep].additionalValidations[extraParamName] !== false;
      });
    }

    if (!valid) {
      console.warn("Contains invalid values in sensitive cstics");
      throw new Error("Pending configurator input is invalid");
    }

    const updateApi = getConfiguratorDefinition(state.productCode).apiCalls.update;
    try {
      await performConfiguratorUpdate(dispatch, getState, (latestState) => updateApi(latestState, updates));
    } catch (error) {
      console.error(error);
      dispatch(hideLoader());
      throw error;
    }
  }, INPUT_VALIDATION_DELAY);
};

function customSegmentLengthSelected(value: boolean, kmatIndex: number): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: CUSTOM_SEGMENT_LENGTH_SELECTED,
      payload: { value, kmatIndex },
    });

    debouncedSendUpdate(SubstepType.GuidewaySet, dispatch, getState);
  };
}

function resetSegmentLengthSelected(): ThunkResult<void> {
  return (dispatch) => {
    dispatch({
      type: CUSTOM_SEGMENT_LENGTH_SELECTED_RESET,
    });
  };
}

export function updateGuidewayPartType(
  kmatIndex: number,
  selectedValue: string,
  owningSubstep: SubstepType,
): ThunkResult<void> {
  // System configurator specific
  return (dispatch, getState) => {
    const changes: CsticUpdate[] = [
      {
        type: KmatType.SubRail,
        value: selectedValue,
        key: CSTIC_PART_SIZING_TYPE_NAME,
        index: kmatIndex,
      },
    ];

    if (selectedValue === CSTIC_PART_SIZING_TYPE_NONE) {
      changes.push({
        type: KmatType.SubRail,
        value: null,
        key: CSTIC_PART_AMOUNT_NAME,
        index: kmatIndex,
      });
    }

    const hybrisUpdates = [...changes];
    const reduxUpdates = [...changes];

    const productCode = getState().linearConfigurator.productCode;
    const updateApi = getConfiguratorDefinition(productCode).apiCalls.update;

    dispatch(customSegmentLengthSelected(true, kmatIndex));
    dispatch(updateCsticsAction(reduxUpdates, owningSubstep));
    dispatch(sendUpdateToHybris((state) => updateApi(state, hybrisUpdates)));
  };
}

function prepareALARChanges(kmatIndex: number, csticName: typeof CSTIC_AL_NAME | typeof CSTIC_AR_NAME, val?: string) {
  // System configurator specific
  const clearValue = val === undefined;

  const userUpdate: CsticUpdate = {
    index: kmatIndex,
    type: KmatType.SubRail,
    key: csticName,
    value: clearValue ? null : val!,
  };

  return {
    reduxUpdates: [userUpdate],
    hybrisUpdates: [userUpdate],
  };
}

export function updateALAR(
  kmatIndex: number,
  csticName: typeof CSTIC_AL_NAME | typeof CSTIC_AR_NAME,
  substep: SubstepType,
  val?: string,
): ThunkResult<void> {
  // System configurator specific
  return (dispatch, getState) => {
    const { reduxUpdates, hybrisUpdates } = prepareALARChanges(kmatIndex, csticName, val);

    dispatch(updateCsticsAction(reduxUpdates, substep));
    debouncedSendUpdate(substep, dispatch, getState, hybrisUpdates);
  };
}

export function updateGuidewayLen(kmatIndex: number, substep: SubstepType, val?: number): ThunkResult<void> {
  // System configurator specific
  return (dispatch, getState) => {
    const userUpdate: CsticUpdate = {
      index: kmatIndex,
      type: KmatType.SubRail,
      key: CSTIC_LENGTH_NAME,
      value: val === undefined ? null : val.toString(),
    };

    dispatch(updateCsticsAction([userUpdate], substep));

    debouncedSendUpdate(substep, dispatch, getState, [userUpdate]);
  };
}

export function updateSubstepAdditionalData(updates: CsticUpdate[], owningSubstep: SubstepType) {
  return {
    type: UPDATE_ADDITIONAL_DATA,
    payload: { updates, owningSubstep },
  };
}

export function updateGuidewayPieceLen(
  kmatType: KmatType,
  kmatIndex: number,
  pieceIndex: number,
  substep: SubstepType,
  val?: number,
  immediate?: boolean,
): ThunkResult<void> {
  return (dispatch, getState) => {
    const userUpdate: CsticUpdate = {
      index: kmatIndex,
      type: kmatType,
      key: GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(pieceIndex + 1),
      value: val === undefined ? null : val.toString(),
    };

    dispatch(updateCsticsAction([userUpdate], substep));

    debouncedSendUpdate(substep, dispatch, getState, [userUpdate]);
    if (immediate) {
      flushPendingInputChanges();
    }
  };
}

export function refreshGuidewayPieceLen(
  kmatType: KmatType,
  kmatIndex: number,
  pieceIndex: number,
  substep: SubstepType,
  immediate?: boolean,
): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const kmat = state.linearConfigurator.kmats[kmatType][kmatIndex];
    const cstic = kmat.cstics[GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(pieceIndex + 1)];
    dispatch(
      updateGuidewayPieceLen(
        kmatType,
        kmatIndex,
        pieceIndex,
        substep,
        cstic.value ? +cstic.value : undefined,
        immediate,
      ),
    );
  };
}

export function updateCsticsAction(updates: CsticUpdate[], owningSubstep: SubstepType) {
  return {
    type: UPDATE_CSTICS,
    payload: { updates, owningSubstep },
  };
}

export function resetGuidewaySet(kmatIndex: number): ThunkResult<void> {
  // System configurator specific
  return (dispatch) => {
    dispatch(customSegmentLengthSelected(false, kmatIndex));
    dispatch(
      sendUpdateToHybris(
        (state) => {
          const resetTargets = prepareGuidewaySetResetTargets(state.kmats[KmatType.SubRail][kmatIndex], kmatIndex);
          return getConfiguratorDefinition(state.productCode).apiCalls.stepReset(state, resetTargets);
        },
        undefined,
        "supersede",
      ),
    );
  };
}

export function editLinearDetails(carriageIndex: number, substepType: SubstepType): ThunkResult<void> {
  return (dispatch) => {
    dispatch({ type: TOGGLE_LINEAR_CARRIAGE_EDIT, payload: { carriageIndex: carriageIndex, statusEdit: true } });
    dispatch(
      updateCsticValues(
        [
          {
            type: KmatType.SubCarriage,
            value: null,
            key: CSTIC_VSKITS_NAME,
            index: carriageIndex,
          },
        ],
        substepType,
      ),
    );
  };
}

export function updateQuantity(newQ: number) {
  return {
    type: UPDATE_QUANTITY,
    payload: newQ,
  };
}

export function updatePriceReference(newPR: string | undefined) {
  return {
    type: UPDATE_PRICE_REFERENCE,
    payload: newPR,
  };
}

export function resetConfiguratorAction(): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const result = await configuratorMutationCoordinator.run(async () => {
      dispatch(showLoader());
      const state = getState().linearConfigurator;
      const definition = getConfiguratorDefinition(state.productCode);
      const payload = await definition.apiCalls.reset(state);
      configuratorMutationCoordinator.beginConfigurationScope(payload.configuration.configId);
      dispatch({
        type: REPLACE_CONFIGURATION_AFTER_RESET,
        payload: {
          data: payload.configuration,
          helpUrl: payload.helpUrl,
          type: definition.type,
        },
      });
    }, "supersede");

    if (result.status === "failed") {
      console.error(result.error);
      dispatch(hideLoader());
    }
  };
}

export function saveConfiguratorAction(name: string): ThunkResult<Promise<SavedConfigurationDTO>> {
  return (dispatch, getState) => {
    const state = getState();
    const productCode = getProductCode(state);
    const configId = getConfigId(state);
    const currentStep = getCurrentStep(state);
    return sendLinearSaveConfig(productCode, configId, name, currentStep);
  };
}

export function resetLinearDetails(carriageIndex: number, statusEdit: boolean): ThunkResult<void> {
  return async (dispatch) => {
    dispatch({ type: TOGGLE_LINEAR_CARRIAGE_EDIT, payload: { carriageIndex, statusEdit } });
    await dispatch(
      sendUpdateToHybris(
        (state) => {
          const resetTargets: ConfiguratorCsticTarget[] = prepareUnitCarriageResetTargets(
            state.kmats[KmatType.SubCarriage][carriageIndex],
            carriageIndex,
            state.kmats[KmatType.Main],
          );
          return getConfiguratorDefinition(state.productCode).apiCalls.stepReset(state, resetTargets);
        },
        undefined,
        "supersede",
      ),
    );
  };
}
